import { apiClient } from "@/lib/api";
import type {
  GeofenceFeature,
  GeofenceFeatureCollection,
  GeofenceWritePayload,
} from "@/types/geofences";
import type {
  CopyWarehousePayload,
  CreateWarehousePayload,
  PaginatedWarehouses,
  UpdateWarehousePayload,
  Warehouse,
  WarehouseState,
  WarehouseSummary,
  WarehouseTripsToday,
} from "@/types/warehouses";

export type { Warehouse, PaginatedWarehouses } from "@/types/warehouses";

export interface GetWarehousesParams {
  page?: number;
  /** Server cap is 200 (the map view asks for that to plot every match). */
  page_size?: number;
  /** Matches name, address and code. */
  search?: string;
  /** Chip filter (TASK-5138). Omit for «Все». */
  state?: WarehouseState;
}

export interface GetWarehouseSummaryParams {
  /** Chip counts follow the search box; `state` is deliberately not accepted. */
  search?: string;
}

/**
 * Fetches a paginated list of warehouses.
 * GET /api/v1/warehouses/
 */
export async function getWarehouses(
  params?: GetWarehousesParams,
): Promise<PaginatedWarehouses> {
  const response = await apiClient.get<PaginatedWarehouses>("/v1/warehouses/", {
    params,
  });
  return response.data;
}

/**
 * Whole-tenant counts for the header line and the filter chips.
 * GET /api/v1/warehouses/summary/
 */
export async function getWarehouseSummary(
  params?: GetWarehouseSummaryParams,
): Promise<WarehouseSummary> {
  const response = await apiClient.get<WarehouseSummary>(
    "/v1/warehouses/summary/",
    { params },
  );
  return response.data;
}

/**
 * Fetches a single warehouse by ID.
 * GET /api/v1/warehouses/{id}/
 */
export async function getWarehouse(id: number): Promise<Warehouse> {
  const response = await apiClient.get<Warehouse>(`/v1/warehouses/${id}/`);
  return response.data;
}

/**
 * Today's trips (routes starting here on the tenant's date) for one warehouse.
 * GET /api/v1/warehouses/{id}/trips-today/
 */
export async function getWarehouseTripsToday(
  id: number,
): Promise<WarehouseTripsToday> {
  const response = await apiClient.get<WarehouseTripsToday>(
    `/v1/warehouses/${id}/trips-today/`,
  );
  return response.data;
}

/**
 * The zones linked to one warehouse, WITH geometry (an unpaginated FeatureCollection).
 * Without `include_geom` the endpoint returns a legacy `[{id, name, color}]` list that
 * vehicles/mobile still rely on — this function always asks for the geometry.
 * GET /api/v1/warehouses/{id}/geofences/?include_geom=1
 */
export async function getWarehouseGeofences(
  id: number,
): Promise<GeofenceFeatureCollection> {
  const response = await apiClient.get<GeofenceFeatureCollection>(
    `/v1/warehouses/${id}/geofences/`,
    { params: { include_geom: 1 } },
  );
  return response.data;
}

/**
 * Creates a zone AND links it to this warehouse in one transaction; other links are
 * kept (unlike the replace-all `POST /warehouses/{id}/geofences/`).
 * POST /api/v1/warehouses/{id}/geofences/new/
 */
export async function createWarehouseGeofence(
  id: number,
  payload: GeofenceWritePayload,
): Promise<GeofenceFeature> {
  const response = await apiClient.post<GeofenceFeature>(
    `/v1/warehouses/${id}/geofences/new/`,
    payload,
  );
  return response.data;
}

/**
 * Creates a new warehouse.
 * POST /api/v1/warehouses/
 */
export async function createWarehouse(
  payload: CreateWarehousePayload,
): Promise<Warehouse> {
  const response = await apiClient.post<Warehouse>("/v1/warehouses/", payload);
  return response.data;
}

/**
 * Partially updates an existing warehouse.
 * PATCH /api/v1/warehouses/{id}/
 */
export async function updateWarehouse(
  id: number,
  payload: UpdateWarehousePayload,
): Promise<Warehouse> {
  const response = await apiClient.patch<Warehouse>(
    `/v1/warehouses/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Duplicates a warehouse (server-side copy; geofences and the default flag are not
 * copied). Pass `{ name }` composed from an i18n key — the server never composes
 * user-facing text (Decision 8). Without a name the copy keeps the original's name.
 * POST /api/v1/warehouses/{id}/copy/
 */
export async function copyWarehouse(
  id: number,
  payload?: CopyWarehousePayload,
): Promise<Warehouse> {
  const response = await apiClient.post<Warehouse>(
    `/v1/warehouses/${id}/copy/`,
    payload ?? {},
  );
  return response.data;
}

/**
 * Deletes a warehouse by ID.
 * DELETE /api/v1/warehouses/{id}/
 */
export async function deleteWarehouse(id: number): Promise<void> {
  await apiClient.delete(`/v1/warehouses/${id}/`);
}
